<template>
    <div class="form-group">
        <label class="form-label section-title">{{ $t('messages.display_geography') }}</label>
        <!-- Панель выбора географии -->
        <div class="geography-selection-panel">
            <!-- Левая панель - выбор -->
            <div class="selection-panel">
                <div class="bordered-container">
                    <!-- Панель управления -->
                    <div class="controls-panel">
                        <div class="search-input">
                            <img src="/images/search.svg" alt="search" class="search-icon" />
                            <input type="text" class="form-input" v-model="searchQuery"
                                :placeholder="$t('messages.search_placeholder')" />
                        </div>
                    </div>

                    <!-- Список географии -->
                    <div class="items-list">
                        <div v-for="item in filteredGeographies" :key="item.id" class="geography-item">
                            <!-- Родительский элемент -->
                            <div class="geography-parent">
                                <button v-if="item.children && item.children.length > 0" class="expand-button"
                                    @click="toggleExpand(item)">
                                    <img :src="expandedItems.has(item.id) ? '/images/arrow-down.svg' : '/images/arrow-right.svg'"
                                        alt="expand" />
                                </button>
                                <div v-else class="expand-spacer"></div>

                                <label class="checkbox">
                                    <input type="checkbox" :checked="isItemSelected(item)" @change="toggleItem(item)" />
                                    <span class="item-name">{{ getItemName(item) }}</span>
                                </label>
                            </div>

                            <!-- Дочерние элементы -->
                            <div v-if="expandedItems.has(item.id) && item.children && item.children.length > 0"
                                class="children-container">
                                <div v-for="child in item.children" :key="child.id" class="geography-child">
                                    <div class="geography-parent">
                                        <button v-if="child.children && child.children.length > 0" class="expand-button"
                                            @click="toggleExpand(child)">
                                            <img :src="expandedItems.has(child.id) ? '/images/arrow-down.svg' : '/images/arrow-right.svg'"
                                                alt="expand" />
                                        </button>
                                        <div v-else class="expand-spacer"></div>

                                        <label class="checkbox">
                                            <input type="checkbox" :checked="isItemSelected(child)"
                                                @change="toggleItem(child)" />
                                            <span class="item-name">{{ getItemName(child) }}</span>
                                        </label>
                                    </div>

                                    <!-- Внуки (города) -->
                                    <div v-if="expandedItems.has(child.id) && child.children && child.children.length > 0"
                                        class="grandchildren-container">
                                        <div v-for="grandchild in child.children" :key="grandchild.id"
                                            class="geography-grandchild">
                                            <label class="checkbox">
                                                <input type="checkbox" :checked="isItemSelected(grandchild)"
                                                    @change="toggleItem(grandchild)" />
                                                <span class="item-name">{{ getItemName(grandchild) }}</span>
                                            </label>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <!-- Правая панель - выбранные -->
            <div class="selected-segments">
                <div class="bordered-container">
                    <div class="selected-header">
                        <h4 class="selected-title">{{ $t('messages.selected') }}</h4>
                        <button class="btn-delete-all" @click="clearAll">
                            {{ $t('messages.delete_all') }}
                        </button>
                    </div>

                    <div class="selected-list">
                        <div v-for="item in selectedItems" :key="item.id" class="selected-item">
                            <span class="item-name">{{ getItemName(item) }}</span>
                            <button class="remove-btn" @click="removeItem(item)">
                                <img src="/images/circle-minus.svg" alt="remove" />
                            </button>
                        </div>
                        <div v-if="selectedItems.length === 0" class="empty-state">
                            {{ $t('messages.no_selected_items') }}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
export default {
    name: "GeographySelector",
    props: {
        geography: {
            type: Array,
            default: () => []
        },
        geographies: {
            type: Array,
            default: () => []
        }
    },
    data() {
        return {
            selectedItems: [],
            searchQuery: '',
            expandedItems: new Set()
        };
    },
    computed: {
        // Строим иерархию из плоского массива географии
        hierarchicalGeographies() {
            if (!this.geographies || this.geographies.length === 0) {
                return [];
            }

            // Создаем копию массива для работы
            const geographies = [...this.geographies];

            // Группируем по parent_id
            const children = {};
            geographies.forEach(geo => {
                const parentId = geo.parent_id || 0;
                if (!children[parentId]) {
                    children[parentId] = [];
                }
                children[parentId].push(geo);
            });

            // Строим иерархию рекурсивно
            const buildHierarchy = (parentId = 0) => {
                const result = [];
                if (children[parentId]) {
                    children[parentId].forEach(item => {
                        item.children = buildHierarchy(item.id);
                        result.push(item);
                    });
                }
                return result;
            };

            return buildHierarchy();
        },

        filteredGeographies() {
            let filtered = this.hierarchicalGeographies;

            if (this.searchQuery) {
                filtered = this.filterBySearch(filtered, this.searchQuery);
            }

            return filtered;
        }
    },
    watch: {
        '$i18n.locale'() {
            // Принудительно обновляем компонент при смене языка
            this.$forceUpdate();
        },
        geography: {
            handler(newGeography) {
                this.initializeSelectedItems(newGeography);
            },
            immediate: true
        }
    },
    methods: {
        initializeSelectedItems(geographyIds) {
            if (!geographyIds || !Array.isArray(geographyIds)) {
                this.selectedItems = [];
                return;
            }

            // Находим элементы по ID в иерархической структуре
            this.selectedItems = [];
            geographyIds.forEach(id => {
                const item = this.findItemById(this.hierarchicalGeographies, id);
                if (item) {
                    this.selectedItems.push(item);
                }
            });
        },

        findItemById(items, id) {
            // Преобразуем id в число для сравнения
            const numericId = parseInt(id);

            for (const item of items) {
                if (item.id === numericId || item.id === id) {
                    return item;
                }
                if (item.children && item.children.length > 0) {
                    const found = this.findItemById(item.children, id);
                    if (found) {
                        return found;
                    }
                }
            }
            return null;
        },

        toggleExpand(item) {
            if (this.expandedItems.has(item.id)) {
                this.expandedItems.delete(item.id);
            } else {
                this.expandedItems.add(item.id);
            }
            this.$forceUpdate();
        },

        toggleItem(item) {
            if (this.isItemSelected(item)) {
                this.removeItem(item);
            } else {
                this.addItem(item);
            }
        },

        addItem(item) {
            if (!this.isItemSelected(item)) {
                this.selectedItems.push(item);
                this.updateGeographyFromSelected();
            }
        },

        removeItem(item) {
            const index = this.selectedItems.findIndex(i => i.id === item.id);
            if (index > -1) {
                this.selectedItems.splice(index, 1);
                this.updateGeographyFromSelected();
            }
        },

        isItemSelected(item) {
            return this.selectedItems.some(i => i.id === item.id);
        },

        clearAll() {
            this.selectedItems = [];
            this.updateGeographyFromSelected();
        },

        getItemName(item) {
            // Возвращаем название в зависимости от текущего языка платформы
            const currentLocale = this.$i18n.locale || 'ru';

            switch (currentLocale) {
                case 'kk':
                    return item.name_kk || item.name_ru || item.name_en || 'Атауы жоқ';
                case 'en':
                    return item.name_en || item.name_ru || item.name_kk || 'No name';
                case 'ru':
                default:
                    return item.name_ru || item.name_en || item.name_kk || 'Без названия';
            }
        },

        filterBySearch(items, query) {
            const lowerQuery = query.toLowerCase();
            const filtered = [];
            const currentLocale = this.$i18n.locale || 'ru';

            items.forEach(item => {
                // Определяем приоритет поиска в зависимости от текущего языка
                let searchFields = [];
                switch (currentLocale) {
                    case 'kk':
                        searchFields = [item.name_kk, item.name_ru, item.name_en];
                        break;
                    case 'en':
                        searchFields = [item.name_en, item.name_ru, item.name_kk];
                        break;
                    case 'ru':
                    default:
                        searchFields = [item.name_ru, item.name_en, item.name_kk];
                        break;
                }

                const itemMatches = searchFields.some(name =>
                    (name || '').toLowerCase().includes(lowerQuery)
                );

                if (itemMatches) {
                    // Если родительский элемент подходит, добавляем его со всеми детьми
                    filtered.push(item);
                } else if (item.children && item.children.length > 0) {
                    // Проверяем детей
                    const filteredChildren = this.filterBySearch(item.children, query);
                    if (filteredChildren.length > 0) {
                        // Если есть подходящие дети, добавляем родителя с отфильтрованными детьми
                        filtered.push({
                            ...item,
                            children: filteredChildren
                        });
                    }
                }
            });

            return filtered;
        },

        updateGeographyFromSelected() {
            // Отправляем массив ID выбранных географий
            const geographyIds = this.selectedItems.map(item => item.id);
            this.updateGeography(geographyIds);
        },

        validateSelection() {
            // Проверяем, что выбрана хотя бы одна география
            return this.selectedItems.length > 0;
        },

        updateGeography(geographyIds = []) {
            this.$emit('update', geographyIds);
        }
    }
};
</script>

<style lang="scss" scoped>
.geography-selection-panel {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 20px;
    margin-top: 16px;
}

.selection-panel {
    flex: 1;
    border-right: 1px solid #e9ecef;
    display: flex;
    flex-direction: column;

    .form-input {
        width: 100%;
        height: 36px;
        background: white;
    }
}

.geography-item {
    margin-bottom: 8px;
}

.geography-parent {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 4px 0;
}

.expand-spacer {
    width: 20px;
    height: 20px;
}

.children-container {
    margin-left: 20px;
    border-left: 1px solid #e9ecef;
    padding-left: 8px;
}

.geography-child {
    margin-bottom: 4px;
}

.grandchildren-container {
    margin-left: 60px;
    border-left: 1px solid #e9ecef;
    padding-left: 8px;
}

.geography-grandchild {
    margin-bottom: 4px;
}

.expand-button {
    background: none;
    border: none;
    cursor: pointer;
    padding: 4px;

    img {
        width: 12px;
        height: 12px;
    }
}

.checkbox {
    display: flex;
    align-items: center;
    gap: 8px;
    cursor: pointer;
    padding: 4px 0;

    input[type="checkbox"] {
        width: 18px;
        height: 18px;
        accent-color: #ec4899;
        cursor: pointer;
    }

    .item-name {
        font-size: 14px;
        color: #495057;
    }
}

.selected-segments {
    flex: 1;
    display: flex;
    flex-direction: column;
}

.selected-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 12px;
    border-bottom: 1px solid #e9ecef;
    background: rgba(245, 245, 245, 1);

    .selected-title {
        font-weight: 600;
        font-size: 16px;
        color: #212529;
        margin: 0;
    }
}

.selected-list {
    height: 400px;
    overflow-y: auto;
    padding: 16px;
}

.selected-item {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 8px 0;
    border-bottom: 1px solid #f8f9fa;

    .item-name {
        font-size: 14px;
        color: #495057;
    }

    .remove-btn {
        background: none;
        border: none;
        cursor: pointer;
        padding: 2px;
        transition: transform 0.2s ease;

        img {
            width: 16px;
            height: 16px;
            opacity: 0.6;
        }

        &:hover {
            transform: scale(1.1);

            img {
                opacity: 1;
            }
        }
    }
}

.empty-state {
    text-align: center;
    color: #6c757d;
    font-style: italic;
    padding: 40px 0;
}

.controls-panel {
    display: flex;
    gap: 12px;
    align-items: center;
    background: rgba(245, 245, 245, 1);
    padding: 12px;

    .search-input {
        .form-input {
            width: 100%;
            height: 36px;
            background: white;
        }
    }
}

.selection-panel,
.selected-segments {
    .bordered-container {
        border: 1px solid #e9ecef;
        border-radius: 2px;
        background: #fff;
    }
}
</style>
